/**
 * VisaGenius wordmark — canonical lockup per the brand sheet.
 *
 *   Visa  → Plus Jakarta Sans, 600 SemiBold, Forest (or surface-tuned)
 *   Genius → Plus Jakarta Sans, 800 ExtraBold, Terracotta (or lifted tint)
 *
 * Use this anywhere the literal "VisaGenius" text appears in chrome —
 * AppShell, headers, footers, sign-in, etc. Prior surfaces inlined the
 * brand as `<span className="font-display">VisaGenius</span>` (Instrument
 * Serif), which drifted from the brand-sheet identity once Pass 1 of the
 * rollout landed.
 *
 * The wordmark sizes itself from `fontSize` so it can sit alongside any
 * existing icon/spark mark at matching heights.
 */
import type { CSSProperties } from "react";

type Variant = "default" | "reversed" | "all-forest" | "all-ink" | "all-white";

const VARIANTS: Record<Variant, { visa: string; genius: string }> = {
  // Full color · Forest "Visa" + Terracotta "Genius". Use on Cream / paper.
  default: { visa: "#2E3A2E", genius: "#B5512F" },
  // Reversed · White "Visa" + lifted-terracotta "Genius". Use on Forest /
  // photography / dark surfaces per the brand-sheet do/don't.
  reversed: { visa: "#FFFFFF", genius: "#E08A6A" },
  // Mono Forest · both words Forest. Use when one tone is required
  // (printed forms, mono-print stationery).
  "all-forest": { visa: "#2E3A2E", genius: "#2E3A2E" },
  // Mono Ink · both words Ink. Use on Sage / Cream when a darker mono
  // reads cleaner than Forest.
  "all-ink": { visa: "#1F1F1C", genius: "#1F1F1C" },
  // Mono White · both words white. Reserved for hero-strength reversed
  // contexts where the Terracotta tint would compete.
  "all-white": { visa: "#FFFFFF", genius: "#FFFFFF" },
};

export function Wordmark({
  size = 20,
  variant = "default",
  className,
  style,
  as: Tag = "span",
}: {
  /** Font size in px. The wordmark scales together (Visa and Genius
   *  share the same size; only the weight changes). */
  size?: number;
  variant?: Variant;
  className?: string;
  style?: CSSProperties;
  as?: "span" | "div";
}) {
  const colors = VARIANTS[variant];
  return (
    <Tag
      className={className}
      style={{
        fontFamily: "'Plus Jakarta Sans', 'Helvetica Neue', system-ui, sans-serif",
        fontSize: size,
        lineHeight: 1,
        letterSpacing: "-0.01em",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      <span style={{ fontWeight: 600, color: colors.visa }}>Visa</span>
      <span style={{ fontWeight: 800, color: colors.genius }}>Genius</span>
    </Tag>
  );
}
